import { useLingui } from '@lingui/react/macro';
import { SearchNormal1 } from 'iconsax-reactjs';
import {
  Fragment,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Input } from './Input';
import { Picker, type PickerProps, type PickerRef } from './Picker';
import { cn, useLazyValue } from './react';
import type { FormControlProps } from './types';

export type SearchableSelectOption<V> = {
  key: string | number;
  value: V;
  disabled?: boolean;
  hidden?: boolean;
  label?: (target: 'option' | 'value') => ReactNode;
  searchText?: string;
};

export type SearchableSelectProps<
  M extends boolean,
  V,
  O = SearchableSelectOption<V>,
> = FormControlProps<M extends true ? V[] : V> &
  PickerProps & {
    multiple?: M;
    options: O[];
    placeholder?: string;
    searchPlaceholder?: string;
    onSearch?: (query: string) => void;
    debounceMs?: number;
    filterOptions?: (options: O[], query: string) => O[];
  };

export const SearchableSelect = <M extends boolean = false, V = unknown>({
  value,
  options,
  multiple,
  onChange,
  placeholder,
  searchPlaceholder,
  onSearch,
  debounceMs = 300,
  filterOptions: customFilterOptions,
  required,
  ...pickerProps
}: SearchableSelectProps<M, V>) => {
  const ref = useRef<PickerRef>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useLazyValue(searchQuery, debounceMs);
  const { t } = useLingui();
  const valueList = Array.isArray(value)
    ? value
    : typeof value !== 'undefined'
      ? [value]
      : [];

  const optionsMap = useMemo(() => {
    const map = new Map<V, SearchableSelectOption<V>>();
    options.forEach((opt) => {
      map.set(opt.value, opt);
    });
    return map;
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return options;
    }

    if (customFilterOptions) {
      return customFilterOptions(options, searchQuery);
    }

    const query = searchQuery.toLowerCase().trim();
    return options.filter((opt) => {
      if (opt.hidden) return false;

      if (opt.searchText) {
        return opt.searchText.toLowerCase().includes(query);
      }

      const keyStr = String(opt.key).toLowerCase();
      return keyStr.includes(query);
    });
  }, [options, searchQuery, customFilterOptions]);

  useEffect(() => {
    if (onSearch && debouncedSearchQuery.trim()) {
      onSearch(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, onSearch]);

  const handleOptionClick = (opt: SearchableSelectOption<V>) => {
    const isChecked = valueList.includes(opt.value as never);

    if (multiple) {
      onChange?.(
        (isChecked
          ? valueList.filter((x) => x !== opt.value)
          : [...valueList, opt.value]) as never,
      );
    } else {
      onChange?.((isChecked && !required ? undefined : opt.value) as never);
      setSearchQuery('');
      ref.current?.[1](false);
    }
  };

  return (
    <Picker
      {...pickerProps}
      ref={ref}
      dialogTitle={placeholder}
      root={
        <>
          {valueList.map((val, i) => (
            <Fragment key={optionsMap.get(val)?.key ?? i.toString()}>
              {optionsMap.get(val)?.label?.('value')}
            </Fragment>
          ))}
          {valueList.length === 0 && (
            <p className="text-content-primary/25 text-sm">{placeholder}</p>
          )}
        </>
      }
      modal={
        <>
          <div className="sticky top-0 z-10 min-w-[255px] bg-background-primary border-b border-border-disabled px-3 py-2">
            <Input
              size={10}
              value={searchQuery}
              onChange={(val: string | undefined) => setSearchQuery(val ?? '')}
              placeholder={searchPlaceholder || t`Search...`}
              prefix={
                <SearchNormal1 size={16} className="text-content-tertiary" />
              }
              className="!rounded-lg"
            />
          </div>

          <div>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isChecked = valueList.includes(opt.value as never);
                return (
                  <button
                    key={opt.key}
                    type="button"
                    className={cn(
                      'block h-14 w-full rounded-none px-4 py-3 enabled:cursor-pointer transition-all text-start',
                      'flex items-center gap-3',
                      isChecked
                        ? 'bg-background-hover/40 border-l-2 border-content-link'
                        : opt.disabled
                          ? 'bg-background-disabled/30 opacity-50'
                          : 'hover:bg-background-hover/30 border-l-2 border-transparent',
                    )}
                    disabled={opt.disabled}
                    onClick={() => handleOptionClick(opt)}
                  >
                    {opt.label?.('option')}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-content-tertiary">
                  {t`No results found`}
                </p>
              </div>
            )}
          </div>
        </>
      }
    />
  );
};
